var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import { chunk } from 'lodash';
import { useMemo, useState } from 'react';
import { sortByLOBName } from '../../common';
import { getPrefixedExpansionKey, getQueueExpansionKey } from '../constants';
import { selectSummaryReport, useSummaryByDisplayMode, useSummaryChunkSize } from '../selectors';
var buildLocationTemplate = function (locations) {
  if (locations === void 0) {
    locations = [];
  }
  var byName = new Map();
  locations.forEach(function (location) {
    if (!byName.has(location.locationName)) {
      byName.set(location.locationName, location);
    }
  });
  return Array.from(byName.values());
};
var buildCountryTemplate = function (queues) {
  var byName = new Map();
  queues.forEach(function (queue) {
    var _a;
    (_a = queue.countries) === null || _a === void 0 ? void 0 : _a.forEach(function (country) {
      var _a;
      var existing = byName.get(country.countryName);
      if (!existing) {
        byName.set(country.countryName, __assign(__assign({}, country), {
          locations: buildLocationTemplate(country.locations)
        }));
        return;
      }
      existing.locations = buildLocationTemplate(__spreadArray(__spreadArray([], existing.locations, true), (_a = country.locations) !== null && _a !== void 0 ? _a : [], true));
    });
  });
  return Array.from(byName.values());
};
var buildQueueTemplate = function (queues) {
  var byName = new Map();
  queues.forEach(function (queue) {
    var queueKey = "".concat(queue.name, ":").concat(queue.queueType);
    var existing = byName.get(queueKey);
    if (!existing) {
      byName.set(queueKey, __assign(__assign({}, queue), {
        countries: buildCountryTemplate([queue])
      }));
      return;
    }
    existing.countries = buildCountryTemplate([existing, queue]);
  });
  return Array.from(byName.values());
};
var buildLobTemplate = function (lobName, perScenarioLob) {
  return {
    lobName: lobName,
    defQueues: buildQueueTemplate(perScenarioLob.flatMap(function (lob) {
      var _a;
      return (_a = lob.defQueues) !== null && _a !== void 0 ? _a : [];
    })),
    nonDefQueues: buildQueueTemplate(perScenarioLob.flatMap(function (lob) {
      var _a;
      return (_a = lob.nonDefQueues) !== null && _a !== void 0 ? _a : [];
    }))
  };
};
export var selectQueuesByLobPerScenario = function (chunkSize) {
  return createSelector(selectSummaryReport, function (summaryReports) {
    if (!(summaryReports === null || summaryReports === void 0 ? void 0 : summaryReports.length)) return [];
    var lobs = Array.from(new Set(summaryReports.flatMap(function (summaryReport) {
      var _a, _b;
      return (_b = (_a = summaryReport.queuesByLob) === null || _a === void 0 ? void 0 : _a.map(function (lob) {
        return lob.lobName;
      })) !== null && _b !== void 0 ? _b : [];
    }))).sort(function (a, b) {
      return sortByLOBName({
        name: a,
        lobName: a
      }, {
        name: b,
        lobName: b
      });
    });
    return lobs.map(function (lobName) {
      var perScenarioLob = summaryReports.map(function (s) {
        var _a;
        var entry = (_a = s.queuesByLob) === null || _a === void 0 ? void 0 : _a.find(function (lob) {
          return lob.lobName === lobName;
        });
        return entry !== null && entry !== void 0 ? entry : {
          lobName: lobName,
          defQueues: [],
          nonDefQueues: []
        };
      });
      return {
        lobName: lobName,
        perScenarioLob: chunk(perScenarioLob, chunkSize),
        template: buildLobTemplate(lobName, perScenarioLob)
      };
    });
  });
};
export var useSelectQueuesByLobPerScenario = function () {
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  var selector = useMemo(function () {
    return selectQueuesByLobPerScenario(chunkSize);
  }, [chunkSize]);
  return selector(data);
};
export var useTableRowExpansion = function (items, defaultExpanded, defaultExpandedItems) {
  if (items === void 0) {
    items = [];
  }
  if (defaultExpanded === void 0) {
    defaultExpanded = false;
  }
  if (defaultExpandedItems === void 0) {
    defaultExpandedItems = [];
  }
  var _a = useState({}),
    expandedMap = _a[0],
    setExpandedMap = _a[1];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var getExpanded = function (item) {
    var _a;
    return (_a = expandedMap[String(item)]) !== null && _a !== void 0 ? _a : defaultExpanded || defaultExpandedItems.includes(item);
  };
  var expandedItems = useMemo(function () {
    return Object.fromEntries(items.map(function (item) {
      return [String(item), getExpanded(item)];
    }));
  }, [items, getExpanded]);
  var someExpanded = useMemo(function () {
    return items.some(getExpanded);
  }, [items, getExpanded]);
  var allExpanded = useMemo(function () {
    return items.length > 0 && items.every(getExpanded);
  }, [items, getExpanded]);
  var allCollapsed = !someExpanded;
  var toggleOne = function (item) {
    var key = String(item);
    setExpandedMap(function (prev) {
      var _a;
      return __assign(__assign({}, prev), (_a = {}, _a[key] = !getExpanded(item), _a));
    });
  };
  var toggleAll = function () {
    var nextExpanded = allCollapsed;
    setExpandedMap(Object.fromEntries(items.map(function (item) {
      return [String(item), nextExpanded];
    })));
  };
  var setItemsExpanded = function (itemsToUpdate, expanded) {
    setExpandedMap(function (prev) {
      return __assign(__assign({}, prev), Object.fromEntries(itemsToUpdate.map(function (item) {
        return [String(item), expanded];
      })));
    });
  };
  return {
    expandedItems: expandedItems,
    allExpanded: allExpanded,
    someExpanded: someExpanded,
    allCollapsed: allCollapsed,
    toggleOne: toggleOne,
    toggleAll: toggleAll,
    setItemsExpanded: setItemsExpanded
  };
};
export function useControlledExpansion(expandedProp, onToggle, defaultExpanded) {
  if (defaultExpanded === void 0) {
    defaultExpanded = true;
  }
  var _a = useState(defaultExpanded),
    localExpanded = _a[0],
    setLocalExpanded = _a[1];
  var isControlled = onToggle !== undefined;
  return {
    expanded: isControlled ? expandedProp !== null && expandedProp !== void 0 ? expandedProp : false : localExpanded,
    handleToggle: isControlled ? onToggle : function () {
      return setLocalExpanded(function (prev) {
        return !prev;
      });
    }
  };
}
export function useAllQueueNames(lobsPerScenario) {
  return useMemo(function () {
    var queueNames = new Set();
    lobsPerScenario.forEach(function (lobEntry) {
      lobEntry.template.defQueues.forEach(function (q) {
        return queueNames.add(q.name);
      });
      lobEntry.template.nonDefQueues.forEach(function (q) {
        return queueNames.add(q.name);
      });
    });
    return Array.from(queueNames);
  }, [lobsPerScenario]);
}
var getQueueExpansionKeys = function (lobName, constraintName, queues, prefix) {
  return queues.map(function (queue) {
    return getPrefixedExpansionKey(prefix, getQueueExpansionKey(lobName, constraintName, queue.name, queue.queueType));
  });
};
export var getCountryExpansionKey = function (queueExpansionKey, countryName) {
  return "".concat(queueExpansionKey, ":country:").concat(countryName);
};
export function getQueueExpansionKeysForLob(lobEntry, prefix) {
  var queueKeys = __spreadArray(__spreadArray([], getQueueExpansionKeys(lobEntry.lobName, 'deferrable', lobEntry.template.defQueues, prefix), true), getQueueExpansionKeys(lobEntry.lobName, 'nonDeferrable', lobEntry.template.nonDefQueues, prefix), true);
  var countryKeys = __spreadArray(__spreadArray([], lobEntry.template.defQueues.map(function (queue) {
    return {
      queue: queue,
      constraintName: 'deferrable'
    };
  }), true), lobEntry.template.nonDefQueues.map(function (queue) {
    return {
      queue: queue,
      constraintName: 'nonDeferrable'
    };
  }), true).flatMap(function (_a) {
    var _b, _c;
    var queue = _a.queue,
      constraintName = _a.constraintName;
    var queueKey = getPrefixedExpansionKey(prefix, getQueueExpansionKey(lobEntry.lobName, constraintName, queue.name, queue.queueType));
    return (_c = (_b = queue.countries) === null || _b === void 0 ? void 0 : _b.map(function (country) {
      return getCountryExpansionKey(queueKey, country.countryName);
    })) !== null && _c !== void 0 ? _c : [];
  });
  return __spreadArray(__spreadArray([], queueKeys, true), countryKeys, true);
}
export function useQueueExpansionKeys(lobsPerScenario, prefix) {
  return useMemo(function () {
    return lobsPerScenario.flatMap(function (lobEntry) {
      return getQueueExpansionKeysForLob(lobEntry, prefix);
    });
  }, [lobsPerScenario, prefix]);
}
export function useMultiskillGroupNames(data) {
  return useMemo(function () {
    var names = new Set();
    data.forEach(function (scenario) {
      var _a;
      (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.forEach(function (q) {
        if (q.name) names.add(q.name);
      });
    });
    return Array.from(names);
  }, [data]);
}
export var getMultiskillCategoryExpansionKey = function (prefix) {
  return getPrefixedExpansionKey(prefix, 'multiSkill');
};
export var getMultiskillGroupExpansionKey = function (name, prefix) {
  return "".concat(getMultiskillCategoryExpansionKey(prefix), ":group:").concat(name);
};
export var getMultiskillGroupCountryExpansionKey = function (name, countryName, prefix) {
  return getCountryExpansionKey(getMultiskillGroupExpansionKey(name, prefix), countryName);
};
export function buildMultiskillGroupExpansionKeys(data, prefix) {
  var keys = new Set();
  data.forEach(function (scenario) {
    var _a;
    (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.forEach(function (group) {
      var _a;
      if (!group.name) return;
      keys.add(getMultiskillCategoryExpansionKey(prefix));
      keys.add(getMultiskillGroupExpansionKey(group.name, prefix));
      (_a = group.countries) === null || _a === void 0 ? void 0 : _a.forEach(function (country) {
        keys.add(getMultiskillGroupCountryExpansionKey(group.name, country.countryName, prefix));
      });
    });
  });
  return Array.from(keys);
}
export function useMultiskillGroupExpansionKeys(data, prefix) {
  return useMemo(function () {
    return buildMultiskillGroupExpansionKeys(data, prefix);
  }, [data, prefix]);
}